import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import {
  alertCaption,
  alertDetailWhen,
  alertNumber,
  alertPlace,
  alertRowWhen,
  inAlertFilter,
  listAllAlerts,
  markAlertRead,
  type Alert,
  type AlertFilter,
  type AlertSeverity,
} from '../net/alerts';
import { ApiError } from '../net/client';
import { listAllProducts } from '../net/products';
import { useSession } from '../session/useSession';
import { useUnreadAlerts } from '../session/unreadAlerts';
import { colors, fonts, plot, radius, shell, type, verdict } from '../theme';

/**
 * G8, `design/PortalAlerts.dc.html`.
 *
 * Every page of `GET /v1/alerts` and every product, once; the three pills
 * filter locally. Mark read is `POST /v1/alerts/:id/read`, and Mark all read
 * is that route once per unread alert loaded, since the API has no bulk
 * route. Each one moves the sidebar badge, which reads the same
 * `unreadCount`. The number, the place, a spike's three counts and its map
 * are drawn where the alert carries them, D40.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; alerts: Alert[]; names: Map<string, string> };

const filters: { key: AlertFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'critical', label: 'Critical' },
];

/** Critical is drawn in the unrecognised red, a warning in the suspect amber. */
const tones: Record<AlertSeverity, { bg: string; fg: string }> = {
  critical: { bg: verdict.unknown.bg, fg: verdict.unknown.fg },
  warning: { bg: verdict.suspect.bg, fg: verdict.suspect.fg },
};

const severityLabels: Record<AlertSeverity, string> = { critical: 'Critical', warning: 'Warning' };

export function Alerts() {
  const { api } = useSession();
  const unread = useUnreadAlerts();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [filter, setFilter] = useState<AlertFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const { set: setUnread } = unread;
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([listAllAlerts(api, signal), listAllProducts(api, signal)]).then(
      ([inbox, products]) => {
        setLoad({
          status: 'loaded',
          alerts: inbox.alerts,
          names: new Map(products.map((p) => [p.id, p.name])),
        });
        setUnread(inbox.unreadCount);
      },
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api, setUnread]);

  const alerts = load.status === 'loaded' ? load.alerts : [];
  const shown = alerts.filter((a) => inAlertFilter(a, filter));
  // As in the reports inbox, the selection outlives its filter: an alert just
  // read stays open under Unread until the next pill press.
  const selected = alerts.find((a) => a.id === selectedId) ?? shown[0];
  const waiting = alerts.filter((a) => a.readAt === undefined);

  const pick = (next: AlertFilter) => {
    setFilter(next);
    setSelectedId(alerts.find((a) => inAlertFilter(a, next))?.id ?? null);
  };

  /** Marks each alert in turn, and stops at the first refusal with what was done kept. */
  const markRead = async (targets: Alert[]) => {
    setBusy(true);
    setRefusal(null);
    const done = new Set<string>();
    try {
      for (const alert of targets) {
        await markAlertRead(api, alert.id);
        done.add(alert.id);
      }
    } catch (error) {
      setRefusal(failureText(error));
    }
    setBusy(false);
    if (done.size === 0) return;
    const readAt = new Date().toISOString();
    setLoad((current) =>
      current.status === 'loaded'
        ? { ...current, alerts: current.alerts.map((a) => (done.has(a.id) ? { ...a, readAt } : a)) }
        : current,
    );
    if (unread.count !== null) setUnread(Math.max(0, unread.count - done.size));
  };

  const now = new Date();
  const nameOf = (alert: Alert) =>
    alert.productId !== undefined && load.status === 'loaded'
      ? load.names.get(alert.productId)
      : undefined;

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading alerts</Waiting> : null}
      <PageHeader
        title="Alerts"
        {...(unread.count !== null
          ? { subtitle: `${unread.count} unread · one alert per problem, counting its occurrences` }
          : {})}
        action={
          <button
            className="vp-outline"
            type="button"
            disabled={busy || waiting.length === 0}
            onClick={() => void markRead(waiting)}
            style={outlineButton}
          >
            Mark all read
          </button>
        }
      />
      {load.status === 'failed' ? (
        <Card title="Alerts could not be loaded" body={failureText(load.error)} />
      ) : (
        <div
          aria-busy={load.status === 'loading'}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.3fr',
            gap: 16,
            flexGrow: 1,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div role="group" aria-label="Filter" style={{ display: 'flex', gap: 8 }}>
              {filters.map((f) => (
                <button
                  className="vp-outline"
                  key={f.key}
                  type="button"
                  aria-pressed={filter === f.key}
                  onClick={() => pick(f.key)}
                  style={pillStyle(filter === f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {load.status === 'loaded' ? (
              <ul
                aria-label="Alerts"
                style={{ ...panel, listStyle: 'none', margin: 0, padding: 0 }}
              >
                {shown.length === 0 ? (
                  <li style={{ ...type.caption, color: colors.mutedInk, padding: '14px 16px' }}>
                    No alerts in this list.
                  </li>
                ) : (
                  shown.map((alert, i) => (
                    <li key={alert.id}>
                      {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
                      <Row
                        alert={alert}
                        product={nameOf(alert)}
                        when={alertRowWhen(alert, now)}
                        selected={alert.id === selected?.id}
                        onSelect={() => {
                          setSelectedId(alert.id);
                          setRefusal(null);
                        }}
                      />
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
          {selected ? (
            <section aria-label="Alert" style={panel}>
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Chip tone={tones[selected.severity]}>{severityLabels[selected.severity]}</Chip>
                  {selected.occurrences > 1 ? (
                    <Chip
                      tone={tones[selected.severity]}
                    >{`${selected.occurrences} occurrences`}</Chip>
                  ) : null}
                  <div style={{ ...type.meta, color: colors.mutedInk }}>
                    {detailLine(selected, now)}
                  </div>
                </div>
                <h2
                  style={{
                    fontFamily: fonts.display,
                    fontWeight: 800,
                    fontSize: '22px',
                    color: colors.navy,
                    margin: 0,
                  }}
                >
                  {selected.title}
                </h2>
                <p style={{ ...type.body, lineHeight: 1.6, color: colors.navy, margin: 0 }}>
                  {selected.message}
                </p>
                {selected.spike ? <SpikeCounts spike={selected.spike} /> : null}
                {selected.type === 'geo_spike' && selected.location ? (
                  <SpikeMap
                    label={alertPlace(selected) ?? ''}
                    coordinates={selected.location.coordinates}
                  />
                ) : null}
                {selected.type === 'geo_spike' || selected.readAt === undefined ? (
                  <div style={{ display: 'flex', gap: 12 }}>
                    {selected.type === 'geo_spike' ? (
                      <button
                        className="vp-fill"
                        type="button"
                        onClick={() => navigate('/reports')}
                        style={primaryButton}
                      >
                        Open on heat map
                      </button>
                    ) : null}
                    {selected.readAt === undefined ? (
                      <button
                        className="vp-outline"
                        type="button"
                        disabled={busy}
                        onClick={() => void markRead([selected])}
                        style={outlineButton}
                      >
                        Mark read
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {refusal !== null ? <Rejection>{refusal}</Rejection> : null}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </>
  );
}

function Row({
  alert,
  product,
  when,
  selected,
  onSelect,
}: {
  alert: Alert;
  product: string | undefined;
  when: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const tone = tones[alert.severity];
  const isUnread = alert.readAt === undefined;
  return (
    <button
      className="vp-outline"
      type="button"
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
      style={rowStyle(selected)}
    >
      <span
        data-part="severity"
        style={{ width: 8, height: 40, borderRadius: 4, background: tone.fg, flexShrink: 0 }}
      />
      <span style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...(isUnread ? type.bodyBold : type.bodyStrong), color: colors.navy }}>
            {alert.title}
          </span>
          {alert.occurrences > 1 ? (
            <span
              style={{
                padding: '2px 9px',
                borderRadius: 10,
                background: tone.bg,
                color: tone.fg,
                ...type.badge,
                fontWeight: 800,
              }}
            >
              {`×${alert.occurrences}`}
            </span>
          ) : null}
        </span>
        <span style={{ ...type.caption, color: colors.mutedInk }}>
          {alertCaption(alert, product, when)}
        </span>
      </span>
      {isUnread ? (
        <span
          role="img"
          aria-label="Unread"
          style={{ width: 8, height: 8, borderRadius: 4, background: colors.brand, flexShrink: 0 }}
        />
      ) : null}
    </button>
  );
}

/** All three side by side, as the board's comment insists: the scans alone mislead. */
function SpikeCounts({ spike }: { spike: NonNullable<Alert['spike']> }) {
  const cells = [
    { value: spike.scans, label: 'suspect scans', lead: false },
    { value: spike.distinctCodes, label: 'different codes', lead: false },
    { value: spike.distinctUsers, label: 'different people', lead: true },
  ];
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12,
        margin: 0,
      }}
    >
      {cells.map((cell) => (
        <div
          key={cell.label}
          style={{
            borderRadius: 12,
            background: cell.lead ? leadGround : colors.ground,
            border: `1px solid ${cell.lead ? colors.brand : colors.line}`,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        >
          <dt
            style={{
              ...(cell.lead ? type.captionBold : type.captionStrong),
              color: cell.lead ? colors.brand : colors.mutedInk,
            }}
          >
            {cell.label}
          </dt>
          <dd
            style={{
              ...type.statValueSm,
              color: cell.lead ? colors.brand : colors.navy,
              margin: 0,
            }}
          >
            {cell.value.toLocaleString('en-US')}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The board's schematic, drawn as it is: the roads are decoration, the pulse
 * stands at the centre, and only the label and the coordinates are the
 * alert's. The cell is the backend's 0.1 degree grid, about 11 km. D40.
 */
function SpikeMap({ label, coordinates }: { label: string; coordinates: [number, number] }) {
  const [lng, lat] = coordinates;
  const where = `${lat.toFixed(2)}, ${lng.toFixed(2)}`;
  return (
    <div
      role="img"
      aria-label={`Map, ${label}, ${where}`}
      style={{
        height: 150,
        borderRadius: 12,
        background: plot.ground,
        overflow: 'hidden',
        border: `1px solid ${colors.line}`,
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 520 180" preserveAspectRatio="xMidYMid slice">
        <rect width="520" height="180" fill={plot.mapLand} />
        <path
          d="M0 120 L120 108 L230 132 L340 100 L440 124 L520 106"
          stroke={plot.mapLandStroke}
          strokeWidth="3"
          fill="none"
        />
        <path d="M140 0 L152 180 M300 0 L286 180" stroke={plot.mapLandStroke} strokeWidth="3" />
        <circle cx="252" cy="92" r="54" fill={verdict.unknown.fg} opacity="0.28" />
        <circle cx="252" cy="92" r="30" fill={verdict.unknown.fg} opacity="0.5" />
        <circle cx="252" cy="92" r="13" fill={verdict.unknown.fg} opacity="0.85" />
        <text
          x="274"
          y="88"
          fontFamily={fonts.text}
          fontSize="13"
          fontWeight="700"
          fill={colors.navy}
        >
          {label}
        </text>
        <text x="274" y="106" fontFamily={fonts.text} fontSize="11" fill={colors.mutedInk}>
          {`${where} · ≈11 km cell`}
        </text>
      </svg>
    </div>
  );
}

function Chip({ tone, children }: { tone: { bg: string; fg: string }; children: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '4px 12px',
        borderRadius: radius.chip,
        background: tone.bg,
        color: tone.fg,
        ...type.captionBold,
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

/** `14:02–17:31 today · Alert #A-2214`, the time alone without a number. */
function detailLine(alert: Alert, now: Date): string {
  const number = alertNumber(alert);
  const when = alertDetailWhen(alert, now);
  return number === null ? when : `${when} · Alert ${number}`;
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

/** The lead count's tint, `PortalAlerts.dc.html:195`, the one board that draws it. */
const leadGround = '#FDF0EA';

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

/** This board's pills: 10px by 18px, 13px, navy when selected. */
const pillStyle = (selected: boolean): CSSProperties => ({
  padding: '10px 18px',
  borderRadius: 20,
  cursor: 'pointer',
  ...(selected
    ? { background: colors.navy, color: colors.white, border: 'none', ...type.metaBold }
    : {
        background: colors.card,
        color: colors.mutedInk,
        border: `1px solid ${colors.line}`,
        ...type.metaStrong,
      }),
});

const rowStyle = (selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '14px 16px 14px 12px',
  border: 'none',
  borderLeft: `4px solid ${selected ? colors.brand : 'transparent'}`,
  background: selected ? colors.ground : 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
});

const actionBase: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};

const primaryButton: CSSProperties = {
  ...actionBase,
  background: colors.brand,
  color: colors.white,
  border: 'none',
};

const outlineButton: CSSProperties = {
  ...actionBase,
  background: colors.card,
  color: colors.navy,
  border: `2px solid ${colors.navy}`,
};
